import { type ReactNode, startTransition, useEffect, useState } from 'react';
import { type CfAccountInfo } from '@chainflip/rpc/types';
import { abbreviate } from '@chainflip/utils/string';
import { isMetamaskSnapsSupported } from '@chainsafe/metamask-polkadot-adapter';
import { useMutation, useQueries, useQuery } from '@tanstack/react-query';
import classNames from 'classnames';
import { MacScrollbar } from 'mac-scrollbar';
import MetamaskLogo from '@/shared/assets/svg/wallet-logos/metamask.svg';
import PolkadotJsLogo from '@/shared/assets/svg/wallet-logos/polkadotjs.svg';
import SubwalletLogo from '@/shared/assets/svg/wallet-logos/subwallet.svg';
import { Link } from '@/shared/components/atoms/Link';
import { DashboardModal } from '@/shared/components/dashboards/Modal';
import LoadingSpinner from '@/shared/components/LoadingSpinner';
import { FlipLabel } from '@/shared/components/molecules/FlipLabel';
import { Tooltip } from '@/shared/components/molecules/Tooltip';
import { fetchAccountInfo } from '@/shared/hooks/useAccountInfo';
import {
  type InjectedAccountWithMeta,
  usePolkadot,
  withTimeout,
  POLKADOT_JS_SOURCE,
  SUBWALLET_JS_SOURCE,
  MM_SNAP_SOURCE,
} from '@/shared/hooks/usePolkadot';
import CloseIcon from '@/shared/icons/flip-ui-kit/large/Close';
import InformationIcon from '@/shared/icons/flip-ui-kit/large/Information';
import WarningTriangleIcon from '@/shared/icons/flip-ui-kit/large/WarningTriangle';
import { accountInfoRpcKey } from '@/shared/queryKeys';
import TokenAmount from '@/shared/utils/TokenAmount';

export type SupportedAccountType = 'liquidity_provider' | 'operator' | 'validator';

const SelectRow = ({
  children,
  onClick,
  tooltip,
  disabled = false,
}: {
  children: ReactNode;
  onClick?: () => void;
  tooltip?: string;
  disabled?: boolean;
}) => (
  <Tooltip content={tooltip} disabled={!tooltip}>
    <button
      className={classNames(
        'flex w-full items-center rounded-md border border-cf-gray-4 p-4 transition ease-out',
        'enabled:bg-cf-gray-3-5 enabled:hover:bg-cf-gray-4 [&>div]:disabled:opacity-40',
      )}
      type="button"
      disabled={!onClick || disabled}
      onClick={onClick}
    >
      <div className="w-full">{children}</div>
    </button>
  </Tooltip>
);

export const SelectPjsAccountList = ({
  onSelect,
  accountTypes,
  selectedExtension,
}: {
  onSelect: (selectedAccount: InjectedAccountWithMeta) => void;
  accountTypes: SupportedAccountType[];
  selectedExtension: PolkadotWallet;
}) => {
  const { injectedAccounts } = usePolkadot();
  const extensionAccounts = injectedAccounts.filter((a) => a.meta.source === selectedExtension);

  const accountInfos = useQueries({
    queries: extensionAccounts.map((acct) => ({
      queryKey: accountInfoRpcKey(acct.idSs58),
      queryFn: () => fetchAccountInfo(acct.idSs58),
      select(data: CfAccountInfo) {
        return {
          ...data,
          flip_balance: TokenAmount.fromAsset(data.flip_balance, 'Flip'),
        };
      },
    })),
  });

  return (
    <div className="flex h-full flex-col justify-between gap-6">
      <MacScrollbar skin="dark" className="max-h-[400px] overflow-y-scroll">
        <div className="flex flex-col gap-2">
          {extensionAccounts
            .map((account, index) => {
              const accountInfo = accountInfos[index].data;

              return {
                account,
                accountInfo,
                disabled:
                  accountInfo?.role !== 'unregistered' &&
                  !accountTypes.some((type) => type === accountInfo?.role),
              };
            })
            .sort((a, b) => {
              if (a.disabled && !b.disabled) return 1;
              if (!a.disabled && b.disabled) return -1;

              return b.accountInfo?.flip_balance?.cmp(a.accountInfo?.flip_balance ?? 0) ?? 0;
            })
            .map(({ account, accountInfo, disabled }) => (
              <SelectRow onClick={() => onSelect(account)} key={account.idSs58} disabled={disabled}>
                <div className="flex w-full flex-col gap-0.5 text-14">
                  <div className="flex items-center justify-between">
                    <span className="text-cf-white">{account.meta.name}</span>
                    {accountInfo?.flip_balance && (
                      <div className="flex items-center gap-1">
                        <FlipLabel amount={accountInfo?.flip_balance} icon precise />
                      </div>
                    )}
                  </div>
                  <span className="text-left text-12 text-cf-light-2">
                    {abbreviate(account.idSs58, 12)}
                  </span>
                  {disabled && accountInfo && (
                    <span className="flex gap-x-1 text-left text-12 text-cf-red-2">
                      <WarningTriangleIcon width="16" height="16" />
                      {accountInfo.role} account cannot be used here
                    </span>
                  )}
                </div>
              </SelectRow>
            ))}
        </div>
      </MacScrollbar>
      <div className="flex items-center gap-2 rounded-md border border-cf-orange-1/20 bg-cf-orange-4 p-3 text-14 text-cf-orange-1">
        <div>
          <WarningTriangleIcon width="24" height="24" />
        </div>
        <div>
          <span className="font-bold">Ledger wallets</span> are not compatible with the Chainflip
          State Chain
        </div>
      </div>
    </div>
  );
};

// wallet extensions taking over window.ethereum identify themselves with flags on the provider
const KNOWN_WALLET_FLAGS = {
  isRabby: 'Rabby',
  isBraveWallet: 'Brave Wallet',
  isCoinbaseWallet: 'Coinbase Wallet',
  isTrust: 'Trust Wallet',
  isOkxWallet: 'OKX Wallet',
  isPhantom: 'Phantom',
  isTalisman: 'Talisman',
  isSubWallet: 'SubWallet',
  isTokenPocket: 'TokenPocket',
  isXDEFI: 'XDEFI Wallet',
  isExodus: 'Exodus',
  isTronLink: 'TronLink',
} as const;

type FlaggedProvider = { [flag in keyof typeof KNOWN_WALLET_FLAGS]?: boolean } & {
  providers?: FlaggedProvider[];
};

const identifyEthereumProvider = (provider: FlaggedProvider | undefined): string | undefined => {
  if (!provider) return undefined;
  const flag = Object.keys(KNOWN_WALLET_FLAGS).find(
    (key) => provider[key as keyof typeof KNOWN_WALLET_FLAGS],
  );
  if (flag) return KNOWN_WALLET_FLAGS[flag as keyof typeof KNOWN_WALLET_FLAGS];

  // some wallets aggregate all injected providers in a providers array
  return provider.providers?.map(identifyEthereumProvider).find(Boolean);
};

// all evm wallet extensions announce themselves through the eip-6963 events, independently
// of which extension won the fight over window.ethereum
const getAnnouncedEvmWallets = () =>
  new Promise<{ name: string; rdns: string }[]>((resolve) => {
    const wallets: { name: string; rdns: string }[] = [];
    const onAnnounce = (event: Event) => {
      const info = (event as CustomEvent<{ info?: { name: string; rdns: string } }>).detail?.info;
      if (info && !wallets.some((wallet) => wallet.rdns === info.rdns)) {
        wallets.push({ name: info.name, rdns: info.rdns });
      }
    };
    window.addEventListener('eip6963:announceProvider', onAnnounce);
    window.dispatchEvent(new Event('eip6963:requestProvider'));
    setTimeout(() => {
      window.removeEventListener('eip6963:announceProvider', onAnnounce);
      resolve(wallets);
    }, 500);
  });

const getIncompatibleMetamaskTooltip = (announcedWallets: { name: string; rdns: string }[]) => {
  const conflictingWallet =
    identifyEthereumProvider(window.ethereum as FlaggedProvider | undefined) ??
    announcedWallets.find((wallet) => !wallet.rdns.startsWith('io.metamask'))?.name;
  const isRealMetamaskInstalled = announcedWallets.some((wallet) =>
    wallet.rdns.startsWith('io.metamask'),
  );

  if (conflictingWallet && isRealMetamaskInstalled) {
    return `${conflictingWallet} is overriding Metamask. Set Metamask as your default wallet or disable ${conflictingWallet}, then reload the page.`;
  }
  if (conflictingWallet) {
    return `${conflictingWallet} is pretending to be Metamask but does not support Metamask Snaps. Install Metamask or disable ${conflictingWallet}, then reload the page.`;
  }
  if (isRealMetamaskInstalled) {
    return 'Metamask is installed but does not respond to Snap requests. Update Metamask to the latest version and reload the page.';
  }
  return 'Metamask is not installed or overwritten by a different wallet extension. Try to disable conflicting wallet extensions.';
};

const SelectMetamaskRow = ({
  isConnecting,
  onSelect,
}: {
  isConnecting: boolean;
  onSelect: () => void;
}) => {
  const isMetamaskInstalled = Boolean(typeof window !== 'undefined' && window.ethereum?.isMetaMask);
  const { data: isSnapSupported, isPending: isSnapSupportedLoading } = useQuery({
    queryKey: ['isMetamaskSnapsSupported'],
    // a corrupted metamask provider leaves rpc requests pending forever, the timeout makes sure
    // we reach the incompatible state instead of showing a connect button that cannot work
    queryFn: () => withTimeout(isMetamaskSnapsSupported(), false),
  });
  const isFakeMetamask = isMetamaskInstalled && !isSnapSupportedLoading && !isSnapSupported;
  const { data: announcedWallets = [] } = useQuery({
    queryKey: ['eip6963Wallets'],
    queryFn: getAnnouncedEvmWallets,
    enabled: isFakeMetamask,
  });

  let text;
  let tooltip;
  let onClick;
  let spinner;
  let disabled = false;

  if (!isMetamaskInstalled) {
    text = 'Install Metamask';
    onClick = () => window.open('https://metamask.io/download/');
  } else if (isFakeMetamask) {
    text = 'Incompatible Metamask';
    tooltip = getIncompatibleMetamaskTooltip(announcedWallets);
    disabled = true;
  } else if (isConnecting) {
    text = 'Approve connection';
    spinner = true;
  } else {
    text = 'Connect Metamask';
    onClick = onSelect;
  }

  return (
    <SelectRow onClick={onClick} disabled={disabled} tooltip={tooltip}>
      <div className="flex w-full items-center justify-between gap-2">
        <div className="flex w-full items-center gap-2 text-14 text-cf-white">
          <div className="flex w-[24px] justify-center">
            <MetamaskLogo />
          </div>
          <div>{text}</div>
        </div>
        {spinner && <LoadingSpinner className="!h-[16px] !w-[16px]" />}
      </div>
    </SelectRow>
  );
};

const walletInfo = {
  [POLKADOT_JS_SOURCE]: {
    installUrl: 'https://polkadot.js.org/extension/',
    displayName: 'Polkadot{.js}',
    logo: <PolkadotJsLogo />,
  },
  [SUBWALLET_JS_SOURCE]: {
    installUrl: 'https://www.subwallet.app/download.html',
    displayName: 'SubWallet',
    logo: (
      <div className="h-6">
        <SubwalletLogo className="origin-top scale-[0.25]" />
      </div>
    ),
  },
} as const;

export type PolkadotWallet = keyof typeof walletInfo;

const SelectPjsRow = ({
  onSelect,
  name,
}: {
  onSelect: (value: PolkadotWallet) => void;
  name: PolkadotWallet;
}) => {
  const {
    injectedExtensions,
    installedWallets,
    rejectedWallets,
    isInitializingExtensions,
    retryConnectWallet,
  } = usePolkadot();
  const isExtInstalled = installedWallets.includes(name);
  const isExtEnabled = injectedExtensions.some((ext) => ext.name === name);
  const isExtRejected = rejectedWallets.includes(name);

  let text;
  let tooltip;
  let onClick;
  let spinner;
  const { displayName, installUrl, logo } = walletInfo[name];

  if (isInitializingExtensions) {
    text = `Initializing ${displayName}`;
    tooltip =
      'Detecting installed Polkadot wallets. Make sure to handle connection requests in your wallet extensions.';
    spinner = true;
  } else if (!isExtInstalled) {
    text = `Install ${displayName}`;
    onClick = () => window.open(installUrl);
  } else if (isExtRejected) {
    text = `Connect ${displayName}`;
    tooltip = `${displayName} declined the connection request. If clicking does not open a new request, allow access for this site in the extension settings and try again.`;
    onClick = () => retryConnectWallet(name);
  } else if (!isExtEnabled) {
    text = 'Approve connection';
    tooltip = `Accept the connection request in the ${displayName} extension, or click to send a new request.`;
    spinner = true;
    onClick = () => retryConnectWallet(name);
  } else {
    text = `Connect ${displayName}`;
    onClick = () => onSelect(name);
  }

  return (
    <SelectRow onClick={onClick} tooltip={tooltip}>
      <div className="flex w-full items-center justify-between gap-2">
        <div className="flex w-full items-center gap-2 text-14 text-cf-white">
          <div className="flex w-[24px] justify-center">{logo}</div>
          <div>{text}</div>
        </div>
        {spinner && <LoadingSpinner className="!h-[16px] !w-[16px]" />}
      </div>
    </SelectRow>
  );
};

export const SelectWalletList = ({
  onSelectWallet,
  onSelectAccount,
}: {
  onSelectWallet: (selectedWallet: PolkadotWallet) => void;
  onSelectAccount: (selectedAccount: InjectedAccountWithMeta) => void;
}) => {
  const { injectedAccounts, connectSnap } = usePolkadot();
  const { mutate: connectMetamaskSnap, isPending: isConnectingMetamaskSnap } = useMutation({
    mutationFn: async () => {
      let snapAccount = injectedAccounts.find((a) => a.meta.source === MM_SNAP_SOURCE);
      if (!snapAccount) {
        snapAccount = await connectSnap();
      }
      onSelectAccount(snapAccount);
    },
  });

  if (isConnectingMetamaskSnap) {
    return (
      <div className="flex flex-col gap-6">
        <SelectMetamaskRow onSelect={connectMetamaskSnap} isConnecting={isConnectingMetamaskSnap} />
        <div className="flex items-center gap-2 rounded-md border border-cf-blue-2/20 bg-cf-blue-5 p-3 text-14 text-cf-blue-2">
          <div>
            <InformationIcon width="24" height="24" />
          </div>
          <div>
            We use Metamask Snaps to install a Polkadot wallet and setup your State Chain account.{' '}
            <Link
              href="https://snaps.metamask.io/snap/npm/chainsafe/polkadot-snap/"
              target="_blank"
              className="underline"
            >
              Learn more
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <SelectMetamaskRow onSelect={connectMetamaskSnap} isConnecting={isConnectingMetamaskSnap} />
      <SelectPjsRow onSelect={onSelectWallet} name={SUBWALLET_JS_SOURCE} />
      <SelectPjsRow onSelect={onSelectWallet} name={POLKADOT_JS_SOURCE} />
    </div>
  );
};

export default function ConnectPolkadotWalletModal({
  open,
  onClose,
  onCompleted,
  accountTypes,
}: {
  open: boolean;
  onClose: () => void;
  onCompleted: (selectedAccount: InjectedAccountWithMeta) => void;
  accountTypes: SupportedAccountType[];
}) {
  const { selectAccount, setShouldFetchExtensionData } = usePolkadot();
  const [selectedExtension, setSelectedExtension] = useState<PolkadotWallet>();

  const onSelectAccount = (account: InjectedAccountWithMeta) => {
    startTransition(() => {
      selectAccount(account);
      onCompleted(account);
    });
  };

  useEffect(() => {
    setShouldFetchExtensionData(open);
  }, [open]);

  return (
    <DashboardModal
      open={open}
      onClose={onClose}
      afterLeave={() => setSelectedExtension(undefined)}
    >
      <div className="relative flex w-[367px] flex-col gap-6 rounded-md border border-cf-gray-4 bg-cf-gray-3 p-6">
        <div className="flex items-center justify-between">
          <div className="font-aeonikMedium text-16 text-cf-white">Connect Wallet</div>
          <button type="button" onClick={onClose} className="outline-cf-gray-5">
            <CloseIcon />
          </button>
        </div>
        {!selectedExtension ? (
          <SelectWalletList
            onSelectWallet={setSelectedExtension}
            onSelectAccount={onSelectAccount}
          />
        ) : (
          <SelectPjsAccountList
            onSelect={onSelectAccount}
            accountTypes={accountTypes}
            selectedExtension={selectedExtension}
          />
        )}
      </div>
    </DashboardModal>
  );
}
